import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import { Save, AddCircleOutline, Edit, Delete } from '@mui/icons-material';
import { RootState } from 'src/redux-store/appStore';
import { useDispatch, useSelector } from 'react-redux';
import { updateFire } from 'src/redux-store/slices/constructionFormSlice';
import toast from 'react-hot-toast';
import { httpRequest } from 'src/services/httpRequest';
import { useParams } from 'react-router-dom';

// Interfaces
interface DeviceItem {
  id?: number;
  deviceType: 'Alarm' | 'Supervisory' | 'Signaling';
  Item: string;
  Quantity: number;
  isEditing?: boolean;
  Type: 'Fire Alarm Device Changes';
}

interface WorkItem {
  name?: string;
  Item: string;
  Size?: number;
  check: boolean;
  Cost: number;
  Quantity?: number;
  WallArea?: string | null;
  FenceHeight?: string | null;
  SignType?: string | null;
  SignArea?: string | null;
  PoolItem?: string | null;
  PoolArea?: string | null;
  WallType?: string | null;
  RoofReplacementType?: string | null;
}

// Add new interface for special devices
interface SpecialDeviceItem {
  id?: number;
  deviceName: string;
  quantity: number;
  Type: 'Fire Alarm Device Changes';
}
interface CostOfWorkProps {
  EstimatedCostOfFireProtectionWork: number;
  onBack: () => void;
  onNext: () => void;
  townshipID: number;
}

const CostOfWork: React.FC<CostOfWorkProps> = ({
  EstimatedCostOfFireProtectionWork,
  onBack,
  onNext,
  townshipID
}) => {
  // Device type options
  const alarmDevices = ['smoke', 'heat', 'pulls', 'water/flow'];
  const supervisoryDevices = ['tampers', 'low/high air'];
  const signalingDevices = ['horn strobes', 'low/bells'];

  const dispatch = useDispatch();
  const {townshipId}= useParams();
  
  const [fireEstimatedCostOfWork, setFireEstimatedCostOfWork] = useState(0);

  // New state for special devices
  const [specialDevices, setSpecialDevices] = useState<SpecialDeviceItem[]>([]);
  const [selectedSpecialDevice, setSelectedSpecialDevice] =
    useState<string>('');
  const [specialDeviceQuantity, setSpecialDeviceQuantity] = useState<number>(0);

  // Special device options
  const specialDeviceOptions = ['110v Interconnected', 'CO Detectors/110v'];

  // State for form inputs
  const [formInput, setFormInput] = useState<DeviceItem>({
    Item: '',
    Quantity: 0,
    deviceType: 'Alarm',
    Type: 'Fire Alarm Device Changes'
  });

  // State for other work items
  const [otherWorkItemsName, setOtherWorkItemsName] = useState([
    { Item: '', Quantity: 0, Fee: 0, check: true, Type: 'other' }
  ]);

  // Fetch existing data from Redux store
  const workItems = useSelector(
    (state: RootState) => state.constructionFormSlice?.Fire?.WorkItems || []
  );

  // Separate devices from regular work items
  const [deviceItems, setDeviceItems] = useState<DeviceItem[]>([]);
  const [regularWorkItems, setRegularWorkItems] = useState<any[]>([]);

  // Load data when component mounts
  useEffect(() => {
    // Set estimated cost
    if (EstimatedCostOfFireProtectionWork) {
      setFireEstimatedCostOfWork(Number(EstimatedCostOfFireProtectionWork));
    }

    // Separate devices from work items when component loads
    if (workItems && workItems.length > 0) {
      const devices: DeviceItem[] = [];
      const regular: WorkItem[] = [];

      workItems.forEach((item: any) => {
        if (item.deviceType) {
          // This is a device item
          devices.push({
            id: devices.length + 1,
            Item: item.Item,
            Quantity: item.Quantity || 0,
            deviceType: item.deviceType,
            Type: 'Fire Alarm Device Changes'
          });
        } else {
          // This is a regular work item
          regular.push(item);
        }
      });
      setDeviceItems(devices);
      setRegularWorkItems(regular);
    }
  }, []);

  // Handle input changes for device items
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormInput((prev) => ({
      ...prev,
      [name]: name === 'Quantity' ? Number(value) : value
    }));
  };

  // Add or update device item
  const handleAddOrUpdateItem = () => {
    // Validate input
    if (!formInput.Item) {
      toast.error('Please select an item');
      return;
    }

    // If editing an existing item
    if (formInput.id !== undefined) {
      setDeviceItems((prev) =>
        prev.map((item) =>
          item.id === formInput.id ? { ...formInput, isEditing: false } : item
        )
      );
    } else {
      // Adding a new item
      const newItem: DeviceItem = {
        ...formInput,
        id: deviceItems.length + 1
      };
      setDeviceItems((prev) => [...prev, newItem]);
    }

    // Reset form
    setFormInput({
      Item: '',
      Quantity: 0,
      deviceType: 'Alarm',
      Type: 'Fire Alarm Device Changes'
    });
  };

  // Edit an item
  const handleEditItem = (item: DeviceItem) => {
    setFormInput({
      ...item,
      isEditing: true
    });
  };

  // Delete an item
  const handleDeleteItem = (id: number) => {
    setDeviceItems((prev) => prev.filter((item) => item.id !== id));
  };

  // Handle work item changes
  const handleWorkItemChange = (
    index: number,
    field: keyof WorkItem,
    value: string | boolean | number
  ) => {
    const updatedWorkItems = [...regularWorkItems];
    updatedWorkItems[index] = { ...updatedWorkItems[index], [field]: value };
    setRegularWorkItems(updatedWorkItems);
  };

  // Get device options based on type
  const getDeviceOptions = (type: 'Alarm' | 'Supervisory' | 'Signaling') => {
    switch (type) {
      case 'Alarm':
        return alarmDevices;
      case 'Supervisory':
        return supervisoryDevices;
      case 'Signaling':
        return signalingDevices;
    }
  };

  // Fetch fire fees
  async function getFireFees() {
    // Combine device items and regular work items for the API call
    const combinedWorkItems = [
      ...regularWorkItems,
      ...deviceItems.map((item) => ({
        ...item,
        check: true
      }))
    ];

    const payload = {
      UseGroup: 'R-3, R-5',
      CertificateType: 'Clearance',
      EstimatedCostOfFireProtectionWork: EstimatedCostOfFireProtectionWork,
      WorkItems: combinedWorkItems,
      TownshipID: townshipId ||townshipID,
    };

    try {
      const response = await httpRequest(
        'POST',
        `/construction/fireProtection/get_fireProtection_fees`,
        payload
      );

      if (response?.status === 'success') {
        const data = response?.data || '';
        const dataDetails = {
          'Fire Size': data['Fire Size'],
          'Certification Cost': data['Certification Cost'],
          totalFee: data.totalFee,
          summary: data.summary
        };

        dispatch(
          updateFire({
            getAllFee: dataDetails
          })
        );
        toast.success('Fire fees retrieved successfully');
      } else {
        toast.error(response?.message || 'Failed to retrieve fire fees');
      }
    } catch (error) {
      toast.error('An error occurred while fetching fire fees');
      console.error(error);
    }
  }

  // Save handler
  const handleSave = async () => {
    // Combine device items and regular work items for Redux
    const combinedWorkItems = [
      ...regularWorkItems,
      ...deviceItems.map((item) => ({
        ...item,
        check: true
      }))
    ];

    // Save to Redux
    dispatch(
      updateFire({
        WorkItems: combinedWorkItems,
        EstimatedCostOfFireProtectionWork: fireEstimatedCostOfWork
      })
    );

    // Fetch fire fees
    await getFireFees();

    // Move to next step
    onNext();
  };

  // New method to handle special device addition
  const handleAddSpecialDevice = () => {
    // Validate input
    if (!selectedSpecialDevice || specialDeviceQuantity <= 0) {
      toast.error('Please select a device and enter a valid quantity');
      return;
    }

    // Check if device already exists
    const existingDeviceIndex = specialDevices.findIndex(
      (device) => device.deviceName === selectedSpecialDevice
    );

    if (existingDeviceIndex !== -1) {
      // Update existing device
      const updatedDevices = [...specialDevices];
      updatedDevices[existingDeviceIndex].quantity = specialDeviceQuantity;
      updatedDevices[existingDeviceIndex].Type = 'Fire Alarm Device Changes';
      setSpecialDevices(updatedDevices);
    } else {
      // Add new device
      const newDevice: SpecialDeviceItem = {
        id: specialDevices.length + 1,
        deviceName: selectedSpecialDevice,
        quantity: specialDeviceQuantity,
        Type: 'Fire Alarm Device Changes'
      };
      setSpecialDevices([...specialDevices, newDevice]);
    }

    // Add to regular work items
    const newWorkItem = {
      Item: selectedSpecialDevice,
      Quantity: specialDeviceQuantity,
      check: true,
      Type: 'Fire Alarm Device Changes'
    };
    setRegularWorkItems([...regularWorkItems, newWorkItem]);

    // Reset inputs
    setSelectedSpecialDevice('');
    setSpecialDeviceQuantity(0);
  };

  // Method to delete special device
  const handleDeleteSpecialDevice = (id: number) => {
    // Remove from special devices
    const updatedSpecialDevices = specialDevices.filter(
      (device) => device.id !== id
    );
    setSpecialDevices(updatedSpecialDevices);

    // Remove from regular work items
    const deviceToRemove = specialDevices.find((device) => device.id === id);
    if (deviceToRemove) {
      const updatedWorkItems = regularWorkItems.filter(
        (item) => item.Item !== deviceToRemove.deviceName
      );
      setRegularWorkItems(updatedWorkItems);
    }
  };

  return (
    <Grid container gap={3}>
      {/* Left Grid - Estimation fields */}
      <Grid item md={3} sm={4}>
        {/* Estimated Cost of Work */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="h5">Estimated Cost of Work</Typography>
          <TextField
            fullWidth
            type="number"
            name="NewBuildingCost"
            value={fireEstimatedCostOfWork}
            onChange={(e) => setFireEstimatedCostOfWork(Number(e.target.value))}
            label={
              <span>
                Fire Work<span style={{ color: 'red' }}>*</span>
              </span>
            }
            variant="outlined"
          />
        </Box>
        <br />

        {/* New Special Devices Section */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 3 }}>
          <Typography variant="h5">Special Alarm Devices</Typography>

          {/* Device Selection */}
          <FormControl fullWidth>
            <InputLabel>Select Special Device</InputLabel>
            <Select
              value={selectedSpecialDevice}
              label="Select Special Device"
              onChange={(e) => setSelectedSpecialDevice(e.target.value)}
            >
              {specialDeviceOptions.map((device) => (
                <MenuItem key={device} value={device}>
                  {device}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Quantity Input */}
          <TextField
            label="Quantity"
            type="number"
            fullWidth
            value={specialDeviceQuantity}
            onChange={(e) => setSpecialDeviceQuantity(Number(e.target.value))}
            inputProps={{ min: 0 }}
          />

          {specialDevices.length > 0 && (
            <Box sx={{ mt: 1 }}>
              {specialDevices.map((device) => (
                <Box
                  key={device.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    borderBottom: '1px solid #e0e0e0',
                    '&:last-child': {
                      borderBottom: 'none'
                    }
                  }}
                >
                  <Box>
                    <Typography component="span" sx={{ fontWeight: 'medium' }}>
                      {device.deviceName}
                    </Typography>
                    <Typography component="span" sx={{ ml: 2 }}>
                      Quantity: {device.quantity}
                    </Typography>
                  </Box>
                  <Box>
                    <IconButton
                      color="primary"
                      onClick={() => {
                        setSelectedSpecialDevice(device.deviceName);
                        setSpecialDeviceQuantity(device.quantity);
                        handleDeleteSpecialDevice(device.id!);
                      }}
                    >
                      <Edit />
                    </IconButton>
                    <IconButton
                      color="error"
                      onClick={() => handleDeleteSpecialDevice(device.id!)}
                    >
                      <Delete />
                    </IconButton>
                  </Box>
                </Box>
              ))}
            </Box>
          )}

          <Button
            variant="contained"
            color="primary"
            startIcon={<AddCircleOutline />}
            onClick={handleAddSpecialDevice}
            disabled={!selectedSpecialDevice}
          >
            Add Device
          </Button>
        </Box>

        <br />

        {/* Device Input Form */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="h5">Alarm System Devices</Typography>

          {/* Device Type Selection */}
          <FormControl fullWidth>
            <InputLabel>Device Type</InputLabel>
            <Select
              name="deviceType"
              label="Device Type"
              value={formInput.deviceType}
              onChange={(e) =>
                setFormInput((prev) => ({
                  ...prev,
                  Item: '', // Reset item when device type changes
                  deviceType: e.target.value as
                    | 'Alarm'
                    | 'Supervisory'
                    | 'Signaling'
                }))
              }
            >
              <MenuItem value="Alarm">Alarm Devices</MenuItem>
              <MenuItem value="Supervisory">Supervisory Devices</MenuItem>
              <MenuItem value="Signaling">Signaling Devices</MenuItem>
            </Select>
          </FormControl>

          {/* Item Selection */}
          <FormControl fullWidth>
            <InputLabel>Select Item</InputLabel>
            <Select
              name="Item"
              label="Select Item"
              value={formInput.Item}
              onChange={handleInputChange as any}
            >
              {getDeviceOptions(formInput.deviceType).map(
                (itemName: string) => (
                  <MenuItem key={itemName} value={itemName}>
                    {itemName}
                  </MenuItem>
                )
              )}
            </Select>
          </FormControl>

          <TextField
            name="Quantity"
            label="Quantity"
            type="number"
            fullWidth
            value={formInput.Quantity}
            onChange={handleInputChange}
          />

          <Button
            variant="contained"
            color={formInput.id !== undefined ? 'secondary' : 'primary'}
            startIcon={
              formInput.id !== undefined ? <Save /> : <AddCircleOutline />
            }
            onClick={handleAddOrUpdateItem}
            disabled={!formInput.Item}
          >
            {formInput.id !== undefined ? 'Update' : 'Add'}
          </Button>
        </Box>
      </Grid>

      <Divider orientation="vertical" variant="middle" flexItem />

      {/* Right Grid - Device and Work Items */}
      <Grid item md={8} sm={6}>
        {/* Work Items */}
        <Typography variant="h5">Fire Work Items</Typography>
        <Grid container spacing={3}>
          {regularWorkItems?.map((item, index) => (
            <Grid
              item
              xs={12}
              md={4}
              sm={6}
              key={`${item.Item}-${index}`}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={item.check}
                    onChange={(e) =>
                      handleWorkItemChange(index, 'check', e.target.checked)
                    }
                  />
                }
                label={item.Item}
              />

              {item.check && (
                <TextField
                  type="number"
                  fullWidth
                  label="Quantity"
                  variant="outlined"
                  value={item.Quantity || 0}
                  onChange={(e) =>
                    handleWorkItemChange(
                      index,
                      'Quantity',
                      Number(e.target.value)
                    )
                  }
                />
              )}
            </Grid>
          ))}
        </Grid>

        {/* Add More Items */}
        <Grid
          item
          xs={12}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            mt: 3
          }}
        >
          <Typography variant="h5">Add More Items</Typography>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'column', md: 'row' },
              gap: 2
            }}
          >
            <TextField
              name="itemName"
              label="Other item name"
              fullWidth
              value={otherWorkItemsName[0].Item}
              onChange={(e) => {
                const updatedWorkItems = [...otherWorkItemsName];
                updatedWorkItems[0].Item = e.target.value;
                setOtherWorkItemsName(updatedWorkItems);
              }}
            />

            <TextField
              label="Quantity"
              name="Quantity"
              type="number"
              fullWidth
              value={otherWorkItemsName[0].Quantity}
              onChange={(e) => {
                if (Number(e.target.value) >= 0) {
                  const updatedWorkItems = [...otherWorkItemsName];
                  updatedWorkItems[0].Quantity = Number(e.target.value);
                  setOtherWorkItemsName(updatedWorkItems);
                }
              }}
            />

            <TextField
              name="Fee"
              label="Fee"
              type="number"
              fullWidth
              value={otherWorkItemsName[0].Fee}
              onChange={(e) => {
                if (Number(e.target.value) >= 0) {
                  const updatedWorkItems = [...otherWorkItemsName];
                  updatedWorkItems[0].Fee = Number(e.target.value);
                  setOtherWorkItemsName(updatedWorkItems);
                }
              }}
            />

            <Button
              variant="contained"
              color={'primary'}
              startIcon={<Save />}
              onClick={() => {
                if (otherWorkItemsName[0].Item !== '') {
                  const newItem = { ...otherWorkItemsName[0] };
                  setRegularWorkItems([...regularWorkItems, newItem]);
                  setOtherWorkItemsName([
                    {
                      Item: '',
                      Quantity: 0,
                      Fee: 0,
                      check: true,
                      Type: 'other'
                    }
                  ]);
                }
              }}
              fullWidth
            >
              {'Add Items'}
            </Button>
          </Box>
        </Grid>

        {/* Devices Table */}
        <Typography variant="h5" mt={5}>
          Installed Devices
        </Typography>
        <TableContainer component={Paper} sx={{ mt: 3, mb: 3 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>Device Type</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Item</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Quantity</TableCell>
                <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {deviceItems.map((item) => (
                <TableRow key={item.id} hover>
                  <TableCell>{item.deviceType}</TableCell>
                  <TableCell>{item.Item}</TableCell>
                  <TableCell>{item.Quantity}</TableCell>
                  <TableCell sx={{ textAlign: 'center' }}>
                    <IconButton
                      color="primary"
                      onClick={() => handleEditItem(item)}
                    >
                      <Edit />
                    </IconButton>
                    <IconButton
                      color="error"
                      onClick={() => handleDeleteItem(item.id!)}
                    >
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>

      {/* Form Action Buttons */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 2,
          mt: 3,
          width: '100%'
        }}
      >
        <Button onClick={onBack} variant="contained" color="inherit">
          Back
        </Button>
        <Button onClick={handleSave} variant="contained">
          Save
        </Button>
      </Box>
    </Grid>
  );
};

export default CostOfWork;
